import React from 'react';
import { Building, Truck, Users, DollarSign, ArrowRight } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';

interface MoneyFlowData {
    totalOrderValue: number;
    supplierPayments: number;
    transporterPayments: number;
    platformEarnings: number;
}

interface MoneyFlowDiagramProps {
    data: MoneyFlowData;
    className?: string;
    animated?: boolean;
}

const formatCurrency = (value: number) => {
    if (value >= 100000) {
        return `₹${(value / 100000).toFixed(2)}L`;
    } else if (value >= 1000) {
        return `₹${(value / 1000).toFixed(1)}K`;
    }
    return `₹${value.toFixed(0)}`;
};

const formatPercentage = (value: number, total: number) => {
    if (total === 0) return '0%';
    return `${((value / total) * 100).toFixed(1)}%`;
};

export function MoneyFlowDiagram({ data, className = '', animated = true }: MoneyFlowDiagramProps) {
    const { totalOrderValue, supplierPayments, transporterPayments, platformEarnings } = data;

    const supplierPercent = totalOrderValue > 0 ? (supplierPayments / totalOrderValue) * 100 : 0;
    const transporterPercent = totalOrderValue > 0 ? (transporterPayments / totalOrderValue) * 100 : 0;
    const platformPercent = totalOrderValue > 0 ? (platformEarnings / totalOrderValue) * 100 : 0;

    return (
        <Card className={className}>
            <CardHeader>
                <CardTitle className="flex items-center gap-2">
                    <DollarSign className="h-5 w-5 text-primary" />
                    Money Flow Distribution
                </CardTitle>
                <CardDescription>
                    How earnings are distributed from each order
                </CardDescription>
            </CardHeader>
            <CardContent>
                <div className="relative py-6">
                    {/* Total Order Value - Source */}
                    <div className="flex justify-center mb-8">
                        <div className="relative">
                            <div className={`
                bg-gradient-to-br from-primary/10 to-primary/5 
                border-2 border-primary rounded-2xl p-6 text-center
                shadow-lg hover:shadow-xl transition-all duration-300
                ${animated ? 'animate-pulse-slow' : ''}
              `}>
                                <p className="text-sm text-muted-foreground font-medium mb-1">Order Total</p>
                                <p className="text-3xl font-bold text-primary">{formatCurrency(totalOrderValue)}</p>
                                <p className="text-xs text-muted-foreground mt-1">100%</p>
                            </div>

                            {/* Animated flow lines */}
                            <div className="absolute left-1/2 -translate-x-1/2 top-full h-8 w-0.5 bg-gradient-to-b from-primary to-transparent" />
                        </div>
                    </div>

                    {/* Flow arrows visualization */}
                    <div className="flex justify-center items-center mb-4">
                        <svg width="300" height="40" className="text-primary/30">
                            <defs>
                                <linearGradient id="flow-gradient" x1="0%" y1="0%" x2="100%" y2="0%">
                                    <stop offset="0%" stopColor="currentColor" stopOpacity="0.1" />
                                    <stop offset="50%" stopColor="currentColor" stopOpacity="0.5" />
                                    <stop offset="100%" stopColor="currentColor" stopOpacity="0.1" />
                                </linearGradient>
                            </defs>
                            {/* Left branch */}
                            <path
                                d="M150,0 Q80,20 40,40"
                                fill="none"
                                stroke="url(#flow-gradient)"
                                strokeWidth="3"
                                className={animated ? 'animate-flow' : ''}
                            />
                            {/* Center */}
                            <path
                                d="M150,0 L150,40"
                                fill="none"
                                stroke="url(#flow-gradient)"
                                strokeWidth="3"
                                className={animated ? 'animate-flow' : ''}
                            />
                            {/* Right branch */}
                            <path
                                d="M150,0 Q220,20 260,40"
                                fill="none"
                                stroke="url(#flow-gradient)"
                                strokeWidth="3"
                                className={animated ? 'animate-flow' : ''}
                            />
                        </svg>
                    </div>

                    {/* Distribution Cards */}
                    <div className="grid grid-cols-3 gap-4">
                        {/* Supplier */}
                        <div
                            className={`
                relative overflow-hidden rounded-xl p-4 text-center
                bg-gradient-to-br from-blue-50 to-blue-100/50 dark:from-blue-900/20 dark:to-blue-800/10
                border border-blue-200 dark:border-blue-800
                hover:shadow-lg transition-all duration-300 hover:-translate-y-1
              `}
                        >
                            <div className="absolute top-0 left-0 right-0 h-1" style={{
                                background: `linear-gradient(to right, transparent, #3b82f6 ${supplierPercent}%, transparent)`
                            }} />
                            <div className="p-2 rounded-full bg-blue-100 dark:bg-blue-900/50 inline-flex mb-3">
                                <Building className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                            </div>
                            <p className="text-sm font-medium text-muted-foreground mb-1">Supplier</p>
                            <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                                {formatCurrency(supplierPayments)}
                            </p>
                            <div className="mt-2 flex items-center justify-center gap-1">
                                <div className="h-2 w-full max-w-[80px] bg-blue-200 dark:bg-blue-900 rounded-full overflow-hidden">
                                    <div
                                        className="h-full bg-blue-600 rounded-full transition-all duration-1000"
                                        style={{ width: `${supplierPercent}%` }}
                                    />
                                </div>
                                <span className="text-xs font-medium text-blue-600 dark:text-blue-400 min-w-[40px]">
                                    {formatPercentage(supplierPayments, totalOrderValue)}
                                </span>
                            </div>
                        </div>

                        {/* Transporter */}
                        <div
                            className={`
                relative overflow-hidden rounded-xl p-4 text-center
                bg-gradient-to-br from-orange-50 to-orange-100/50 dark:from-orange-900/20 dark:to-orange-800/10
                border border-orange-200 dark:border-orange-800
                hover:shadow-lg transition-all duration-300 hover:-translate-y-1
              `}
                        >
                            <div className="absolute top-0 left-0 right-0 h-1" style={{
                                background: `linear-gradient(to right, transparent, #f97316 ${transporterPercent}%, transparent)`
                            }} />
                            <div className="p-2 rounded-full bg-orange-100 dark:bg-orange-900/50 inline-flex mb-3">
                                <Truck className="h-5 w-5 text-orange-600 dark:text-orange-400" />
                            </div>
                            <p className="text-sm font-medium text-muted-foreground mb-1">Transporter</p>
                            <p className="text-2xl font-bold text-orange-600 dark:text-orange-400">
                                {formatCurrency(transporterPayments)}
                            </p>
                            <div className="mt-2 flex items-center justify-center gap-1">
                                <div className="h-2 w-full max-w-[80px] bg-orange-200 dark:bg-orange-900 rounded-full overflow-hidden">
                                    <div
                                        className="h-full bg-orange-600 rounded-full transition-all duration-1000"
                                        style={{ width: `${transporterPercent}%` }}
                                    />
                                </div>
                                <span className="text-xs font-medium text-orange-600 dark:text-orange-400 min-w-[40px]">
                                    {formatPercentage(transporterPayments, totalOrderValue)}
                                </span>
                            </div>
                        </div>

                        {/* Platform */}
                        <div
                            className={`
                relative overflow-hidden rounded-xl p-4 text-center
                bg-gradient-to-br from-green-50 to-green-100/50 dark:from-green-900/20 dark:to-green-800/10
                border border-green-200 dark:border-green-800
                hover:shadow-lg transition-all duration-300 hover:-translate-y-1
              `}
                        >
                            <div className="absolute top-0 left-0 right-0 h-1" style={{
                                background: `linear-gradient(to right, transparent, #22c55e ${platformPercent * 10}%, transparent)`
                            }} />
                            <div className="p-2 rounded-full bg-green-100 dark:bg-green-900/50 inline-flex mb-3">
                                <Users className="h-5 w-5 text-green-600 dark:text-green-400" />
                            </div>
                            <p className="text-sm font-medium text-muted-foreground mb-1">Platform</p>
                            <p className="text-2xl font-bold text-green-600 dark:text-green-400">
                                {formatCurrency(platformEarnings)}
                            </p>
                            <div className="mt-2 flex items-center justify-center gap-1">
                                <div className="h-2 w-full max-w-[80px] bg-green-200 dark:bg-green-900 rounded-full overflow-hidden">
                                    <div
                                        className="h-full bg-green-600 rounded-full transition-all duration-1000"
                                        style={{ width: `${Math.min(platformPercent * 5, 100)}%` }} // Scale for visibility
                                    />
                                </div>
                                <span className="text-xs font-medium text-green-600 dark:text-green-400 min-w-[40px]">
                                    {formatPercentage(platformEarnings, totalOrderValue)}
                                </span>
                            </div>
                        </div>
                    </div>

                    {/* Summary Footer */}
                    <div className="mt-6 pt-4 border-t flex justify-between items-center text-sm text-muted-foreground">
                        <div className="flex items-center gap-2">
                            <div className="w-3 h-3 rounded-full bg-blue-500" />
                            <span>Supplier: Product value</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <div className="w-3 h-3 rounded-full bg-orange-500" />
                            <span>Transporter: Delivery fee</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <div className="w-3 h-3 rounded-full bg-green-500" />
                            <span>Platform: Commission</span>
                        </div>
                    </div>
                </div>
            </CardContent>

            {/* Add custom animation styles */}
            <style>{`
        @keyframes pulse-slow {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.85; }
        }
        .animate-pulse-slow {
          animation: pulse-slow 3s ease-in-out infinite;
        }
        @keyframes flow {
          0% { stroke-dashoffset: 100; }
          100% { stroke-dashoffset: 0; }
        }
        .animate-flow {
          stroke-dasharray: 10 5;
          animation: flow 2s linear infinite;
        }
      `}</style>
        </Card>
    );
}

// Simple inline version for embedding in other components
export function MoneyFlowInline({ data }: { data: MoneyFlowData }) {
    const { totalOrderValue, supplierPayments, transporterPayments, platformEarnings } = data;

    return (
        <div className="flex items-center gap-3 text-sm">
            <div className="flex items-center gap-1.5 px-2 py-1 rounded-md bg-blue-50 dark:bg-blue-900/30">
                <Building className="h-3.5 w-3.5 text-blue-600" />
                <span className="font-medium text-blue-700 dark:text-blue-400">{formatCurrency(supplierPayments)}</span>
            </div>
            <ArrowRight className="h-3 w-3 text-muted-foreground" />
            <div className="flex items-center gap-1.5 px-2 py-1 rounded-md bg-orange-50 dark:bg-orange-900/30">
                <Truck className="h-3.5 w-3.5 text-orange-600" />
                <span className="font-medium text-orange-700 dark:text-orange-400">{formatCurrency(transporterPayments)}</span>
            </div>
            <ArrowRight className="h-3 w-3 text-muted-foreground" />
            <div className="flex items-center gap-1.5 px-2 py-1 rounded-md bg-green-50 dark:bg-green-900/30">
                <Users className="h-3.5 w-3.5 text-green-600" />
                <span className="font-medium text-green-700 dark:text-green-400">{formatCurrency(platformEarnings)}</span>
            </div>
        </div>
    );
}

export default MoneyFlowDiagram;
